// BadgeConfigurations.tsx
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "../MeDasboard/MeAchievements/Achievement.css";
import "./BadgeConfigurations.css";
import styled from "styled-components";
import { notification } from "antd";
import HistoryModal from "../utils/HistoryModal";
import { FaHistory } from "react-icons/fa";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import Cropper from "react-easy-crop";

// Styled component for crop modal
const CropModalContainer = styled.div`
  position: fixed;
  inset: 0;
  background: black;
  z-index: 3000;
  display: flex;
  flex-direction: column;

  .crop-wrap {
    flex: 1;
    position: relative;
  }

  .controls {
    height: 100px;
    background: #222;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 20px;

    input[type="range"] {
      width: 80%;
      max-width: 400px;
    }

    .buttons {
      display: flex;
      gap: 16px;
      button {
        padding: 8px 24px;
        border-radius: 20px;
        border: none;
        background: white;
        font-weight: 600;
        cursor: pointer;
        &.cancel {
          background: #444;
          color: white;
        }
      }
    }
  }
`;

const BadgeConfigurations: React.FC = () => {
  useCheckSessionHeartBeat();
  const [badges, setBadges] = useState<any[]>([]);
  const [isBadgeModalOpen, setIsBadgeModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [loadingRules, setLoadingRules] = useState(false);
  const [badgeData, setBadgeData] = useState({
    badge_uid: "",
    badge_name: "",
    badge_type: "",
    badge_category: "",
    badge_description: "",
    badge_points: 0,
    badge_image: "",
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newBadge, setNewBadge] = useState({
    badge_name: "",
    badge_description: "",
    badge_image: "",
    badge_category: "fitness",
    badge_type: "step",
    badge_rules: [] as string[],
    badge_points: "" as number | string,
  });

  // Image cropping states
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [tempImageForCrop, setTempImageForCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  // Rules state
  const [rules, setRules] = useState<any[]>([]);
  const [filteredRules, setFilteredRules] = useState<any[]>([]);
  const [selectedRule, setSelectedRule] = useState<string>("");

  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const orgUid = localStorage.getItem("org_uid") || "";
  const [selectedBadge, setSelectedBadge] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyUid, setHistoryUid] = useState<string | null>(null);
  const [carousalHistoryMap, setCarousalHistoryMap] = useState<
    Record<string, string | null>
  >({});

  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const fetchBadges = async () => {
    try {
      setLoadingBadges(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/badges`,
        {
          headers: {
            Accept: "application/json",
            "user-uid": userUid,
          },
          withCredentials: true,
        },
      );
      const orgBadges = response.data.data.org_badges || [];
      setBadges(orgBadges);
      buildCarousalHistoryMap(orgBadges);
    } catch (err) {
      console.error("Failed to load badges", err);
    } finally {
      setLoadingBadges(false);
    }
  };

  const fetchRules = async () => {
    setLoadingRules(true);
    try {
      const res = await axios.get(`${beAddress}/api/rules-config`, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });
      if (res.data?.data) {
        setRules(res.data.data);
        // Filter rules based on selected badge type
        filterRulesByType(res.data.data, newBadge.badge_type);
      } else {
        setRules([]);
        setFilteredRules([]);
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
      notification.error({ message: "Failed to fetch rules" });
    } finally {
      setLoadingRules(false);
    }
  };

  const filterRulesByType = (rulesList: any[], badgeType: string) => {
    if (!badgeType) {
      setFilteredRules([]);
      return;
    }

    const filtered = rulesList.filter(
      (rule) => rule.rule_sub_type?.toLowerCase() === badgeType.toLowerCase(),
    );
    setFilteredRules(filtered);
  };

  const buildCarousalHistoryMap = (badges: any[]) => {
    const historyMap: Record<string, string | null> = {};
    badges.forEach((badge: any) => {
      historyMap[badge.badge_uid] = badge.history_uid || null;
    });
    setCarousalHistoryMap(historyMap);
  };

  useEffect(() => {
    fetchBadges();
  }, []);

  useEffect(() => {
    if (isAddModalOpen) {
      fetchRules();
    }
  }, [isAddModalOpen]);

  useEffect(() => {
    filterRulesByType(rules, newBadge.badge_type);
    // Clear selected rule when type changes
    setSelectedRule("");
    setNewBadge((prev) => ({ ...prev, badge_rules: [] }));
  }, [newBadge.badge_type, rules]);

  const openBadgeModal = (badge?: any) => {
    setIsEditing(!!badge);
    setBadgeData(
      badge || {
        badge_uid: "",
        badge_name: "",
        badge_type: "",
        badge_category: "",
        badge_description: "",
        badge_points: 0,
        badge_image: "",
      },
    );
    setIsBadgeModalOpen(true);
  };

  const openViewModal = (badge: any) => {
    setSelectedBadge(badge);
    setIsViewModalOpen(true);
  };

  const updateBadgeData = (key: string, value: any) => {
    setBadgeData((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(",")[1];
      updateBadgeData("badge_image", base64);
    };
    reader.readAsDataURL(file);
  };

  const submitBadge = async () => {
    const payload = { ...badgeData, org_uid: orgUid };
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/add-badge`,
        payload,
        {
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
        },
      );
      setIsBadgeModalOpen(false);
      fetchBadges();
    } catch {
      alert("Failed to save badge");
    }
  };

  const deleteBadge = async (uid: string) => {
    if (!window.confirm("Are you sure you want to delete this badge?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/delete-badge`, {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
        data: { badge_uid: uid, org_uid: orgUid },
      });
      fetchBadges();
    } catch {
      alert("Failed to delete badge");
    }
  };

  // Updated image handler with validation and crop modal
  const handleNewBadgeImage = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // File size validation (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      notification.error({
        message: "Image size exceeds 5MB limit. Please choose a smaller image.",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // File type validation
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      notification.error({
        message: "Please upload only JPG or PNG images.",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Read file and open crop modal
    const readFileAsDataURL = (inputFile: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("FileReader error"));
        reader.readAsDataURL(inputFile);
      });

    const loadImage = (src: string): Promise<HTMLImageElement> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Image failed to load"));
        img.src = src;
      });

    try {
      const base64String = await readFileAsDataURL(file);
      const img = await loadImage(base64String);
      setTempImageForCrop(base64String);
      setIsCropModalOpen(true);
    } catch (err) {
      console.error("Image upload error", err);
      notification.error({ message: "Failed to load image." });
    }
  };

  // Handle crop save
  const handleCropSave = async () => {
    if (!tempImageForCrop || !croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(
        tempImageForCrop,
        croppedAreaPixels,
      );

      // Set the cropped image
      setNewBadge((prev) => ({ ...prev, badge_image: croppedImage }));

      notification.success({
        message: "Image cropped successfully!",
      });
    } catch (error) {
      console.error("Error saving crop", error);
      notification.error({
        message: "An error occurred while cropping the image.",
      });
    }

    setIsCropModalOpen(false);
    setTempImageForCrop(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Get cropped image
  const getCroppedImg = (imageSrc: string, pixelCrop: any): Promise<string> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("No ctx");
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height,
        );
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      };
      image.onerror = reject;
    });
  };

  const handleRuleSelection = (ruleUid: string) => {
    setSelectedRule(ruleUid);

    // Update newBadge with selected rule (as array with single item)
    setNewBadge((prevBadge) => ({
      ...prevBadge,
      badge_rules: ruleUid ? [ruleUid] : [],
    }));
  };

  const submitNewBadge = async () => {
    setIsSubmitting(true);
    try {
      const title = newBadge.badge_name?.trim() || "";
      const description = newBadge.badge_description?.trim() || "";
      const points = parseInt(newBadge.badge_points?.toString() || "0");

      // Validation
      if (title.length === 0 || title.length > 50) {
        notification.error({
          message: "Title must be between 1 and 50 characters.",
        });
        setIsSubmitting(false);
        return;
      }

      if (description.length === 0 || description.length > 200) {
        notification.error({
          message: "Description must be between 1 and 200 characters.",
        });
        setIsSubmitting(false);
        return;
      }

      if (isNaN(points) || points < 50 || points > 1000) {
        notification.error({
          message: "Points must be a number between 50 and 1000.",
        });
        setIsSubmitting(false);
        return;
      }

      if (!newBadge.badge_category || !newBadge.badge_type) {
        notification.error({
          message: "Badge category and type are required.",
        });
        setIsSubmitting(false);
        return;
      }

      // Image validation - make it mandatory
      if (!newBadge.badge_image) {
        notification.error({
          message: "Badge image is required. Please upload an image.",
        });
        setIsSubmitting(false);
        return;
      }

      if (!selectedRule) {
        notification.error({
          message: "Please select a rule for this badge.",
        });
        setIsSubmitting(false);
        return;
      }

      // Extract base64 data if it's a data URL
      let imageData = newBadge.badge_image;
      if (imageData && imageData.startsWith("data:")) {
        imageData = imageData.split(",")[1];
      }

      const payload = {
        ...newBadge,
        badge_name: title,
        badge_description: description,
        badge_category: newBadge.badge_category.toLowerCase(),
        badge_type: newBadge.badge_type.toLowerCase(),
        badge_points: points,
        badge_image: imageData,
        org_uid: orgUid,
        badge_rules: [selectedRule], // Send selected rule UID as array with single item
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/add-badges`,
        payload,
        {
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
        },
      );

      const resData = response.data;
      if (resData?.status === "Failed") {
        notification.error({
          message: resData?.message || "Failed to add badge.",
          description: resData?.data || undefined,
        });
        setIsSubmitting(false);
        return;
      }

      notification.success({ message: "Badge created successfully!" });
      setIsAddModalOpen(false);
      // Reset form
      setNewBadge({
        badge_name: "",
        badge_description: "",
        badge_image: "",
        badge_category: "fitness",
        badge_type: "step",
        badge_rules: [],
        badge_points: "",
      });
      setSelectedRule("");
      fetchBadges();
    } catch (error) {
      console.error("Add badge error:", error);
      notification.error({ message: "Failed to add badge. Try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format rule for display in dropdown
  const formatRuleOption = (rule: any) => {
    const conditions = rule.condition || [];
    const conditionText = conditions
      .map((c: any) => `${c.operator} ${c.value}`)
      .join(" and ");

    return {
      uid: rule.rule_uid,
      displayText: `UID: ${rule.rule_uid} | Operation: ${conditionText || "No conditions"}`,
    };
  };

  return (
    <div className="achievement-page">
      <h2 style={{ textAlign: "center", marginBottom: "1rem" }}>
        Badges Configuration
      </h2>
      <AddBadgeButton onClick={() => setIsAddModalOpen(true)}>
        + Add Badge
      </AddBadgeButton>

      {loadingBadges ? (
        <div className="badge-placeholder">Loading badges...</div>
      ) : badges.length === 0 ? (
        <div className="badge-placeholder">No badges to display</div>
      ) : (
        <div className="badge-grid" style={{ gap: "4.5rem" }}>
          {badges.map((badge) => (
            <div
              className="badge-card achieved"
              onClick={() => openViewModal(badge)}
              key={badge.badge_uid}
            >
              <div className="badge-image-wrapper">
                <img
                  src={`data:image/jpeg;base64,${badge.badge_image}`}
                  alt={badge.badge_name}
                  className="badge-image"
                />
              </div>
              <div className="badge-name">{badge.badge_name}</div>
            </div>
          ))}
        </div>
      )}

      {isViewModalOpen && selectedBadge && (
        <div className="achievement-modal">
          <div
            className="achievement-modal-content"
            style={{ position: "relative" }}
          >
            <FaHistory
              className="bagdes-history-btn"
              onClick={() => {
                const historyId = carousalHistoryMap[selectedBadge.badge_uid];
                setHistoryUid(historyId);
                setShowHistoryModal(true);
              }}
              style={{
                position: "absolute",
                top: "1%",
                left: "-30.5%",
                fontSize: "18px",
                color: "var(--menu-item-color)",
                cursor: "pointer",
              }}
              title="View History"
            />
            <span
              className="achievement-close-btn"
              onClick={() => setIsViewModalOpen(false)}
            >
              &times;
            </span>

            <div
              style={{
                marginTop: "30px",
                fontWeight: "bold",
                letterSpacing: "1px",
              }}
            >
              {selectedBadge.badge_name}
            </div>

            <img
              src={`data:image/jpeg;base64,${selectedBadge.badge_image}`}
              alt={selectedBadge.badge_name || "Badge"}
              style={{
                width: "70px",
                height: "auto",
                objectFit: "cover",
                marginTop: "20px",
              }}
            />

            <div className="badge-description">
              <div className="badge-individual-content">
                <span className="badge-label">Description:</span>{" "}
                {selectedBadge.badge_description}
              </div>
              <div className="badge-individual-content">
                <span className="badge-label">Category:</span>{" "}
                {selectedBadge.badge_category}
              </div>
              <div className="badge-individual-content">
                <span className="badge-label">Type:</span>{" "}
                {selectedBadge.badge_type}
              </div>
              <div className="badge-individual-content">
                <span className="badge-label">Points:</span>{" "}
                {selectedBadge.badge_points}
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <div className="achievement-modal">
          <div className="add-badge-modal">
            {/* Centered Heading */}
            <h3
              style={{
                textAlign: "center",
                width: "100%",
                marginBottom: "1rem",
              }}
            >
              Add New Badge
            </h3>

            {/* Image Preview + Upload with cropping */}
            <div className="add-badge-preview">
              {newBadge.badge_image ? (
                <img src={newBadge.badge_image} alt="Preview" />
              ) : (
                <div
                  style={{
                    width: "180px",
                    height: "180px",
                    backgroundColor: "#f8f8f8",
                    border: "1px dashed #ccc",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#aaa",
                    fontSize: "13px",
                    marginBottom: "10px",
                    textAlign: "center",
                    padding: "0 6px",
                  }}
                >
                  No image selected
                </div>
              )}
              <label className="styled-file-upload">
                Choose Image
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".jpg,.jpeg,.png"
                  onChange={handleNewBadgeImage}
                />
              </label>
              {!newBadge.badge_image && (
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  * JPG/PNG max size 5MB
                </div>
              )}
            </div>

            {/* Form */}
            <div className="add-badge-form">
              <input
                maxLength={40}
                placeholder="Badge Name"
                value={newBadge.badge_name}
                onChange={(e) =>
                  setNewBadge({ ...newBadge, badge_name: e.target.value })
                }
              />

              <textarea
                maxLength={150}
                placeholder="Description"
                value={newBadge.badge_description}
                onChange={(e) =>
                  setNewBadge({
                    ...newBadge,
                    badge_description: e.target.value,
                  })
                }
              />

              <select
                value={newBadge.badge_category}
                onChange={(e) =>
                  setNewBadge({ ...newBadge, badge_category: e.target.value })
                }
              >
                <option value="">Select Category</option>
                <option value="fitness">Fitness</option>
              </select>

              <select
                value={newBadge.badge_type}
                onChange={(e) =>
                  setNewBadge({
                    ...newBadge,
                    badge_type: e.target.value.toLowerCase(),
                  })
                }
              >
                <option value="">Select Type</option>
                <option value="steps">Steps</option>
                <option value="distance">Distance</option>
                <option value="caloriesburned">Calories</option>
              </select>

              {/* Rules Dropdown - Only shown when badge type is selected */}
              {newBadge.badge_type && (
                <div className="rules-dropdown-container">
                  {/* <label className="rules-label">Select Rule:</label> */}
                  {loadingRules ? (
                    <div className="rules-loading">Loading rules...</div>
                  ) : filteredRules.length === 0 ? (
                    <div className="no-rules-message">No rules found</div>
                  ) : (
                    <select
                      className="rules-dropdown"
                      value={selectedRule}
                      onChange={(e) => handleRuleSelection(e.target.value)}
                    >
                      <option value="">Select a rule</option>
                      {filteredRules.map((rule) => {
                        const option = formatRuleOption(rule);
                        return (
                          <option key={rule.rule_uid} value={rule.rule_uid}>
                            {option.displayText}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              )}

              <input
                type="number"
                placeholder="Enter Points (50-1000)"
                min="50"
                max="1000"
                value={newBadge.badge_points}
                onChange={(e) =>
                  setNewBadge({ ...newBadge, badge_points: e.target.value })
                }
              />

              <div className="badge-modal-actions">
                <button
                  className="create-btn"
                  onClick={submitNewBadge}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span
                      className="spinner"
                      style={{
                        display: "inline-block",
                        width: "16px",
                        height: "16px",
                        border: "2px solid #fff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    ></span>
                  ) : (
                    "Create"
                  )}
                </button>

                <button
                  className="cancel-btn"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    // Reset form
                    setNewBadge({
                      badge_name: "",
                      badge_description: "",
                      badge_image: "",
                      badge_category: "fitness",
                      badge_type: "step",
                      badge_rules: [],
                      badge_points: "",
                    });
                    setSelectedRule("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IMAGE CROP MODAL */}
      {isCropModalOpen && tempImageForCrop && (
        <CropModalContainer>
          <div className="crop-wrap">
            <Cropper
              image={tempImageForCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          </div>
          <div className="controls">
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
            <div className="buttons">
              <button onClick={handleCropSave}>Save</button>
              <button
                className="cancel"
                onClick={() => {
                  setIsCropModalOpen(false);
                  setTempImageForCrop(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </CropModalContainer>
      )}

      {/* Move HistoryModal outside of all conditional rendering and ensure it has high z-index */}
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        params={{ history_uid: historyUid! }}
      />
    </div>
  );
};

export default BadgeConfigurations;

const AddBadgeButton = styled.button`
  background-color: var(--sidebar-hover-color);
  color: var(--text-color);
  font-weight: 600;
  font-size: 14px;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: var(--sidebar-hover-color);
  }
`;

const BadgeActionButton = styled.button`
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: ${(props) =>
    props.color === "edit" ? "#28c7fa" : "#e74c3c"};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  &:hover {
    opacity: 0.85;
  }
`;

const ModalButton = styled.button`
  padding: 8px 14px;
  background-color: #003b49;
  color: white;
  font-weight: bold;
  border: none;
  border-radius: 6px;
  margin: 0 0.5rem;
  cursor: pointer;
  &:hover {
    background-color: #005366;
  }
`;
